import { useCallback, useEffect, useRef } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import useGetAgentByFriendlyUrl from '~/features/agents/hooks/useGetAgentByFriendlyUrl'
import useGetAgentByLegacyChatbotId from '~/features/agents/hooks/useGetAgentByLegacyChatbotId'
import useGetGuestAgent from '~/features/agents/hooks/useGetGuestAgent'
import { TChatbotAgent } from '~/features/agents/models'
import {
  EMBEDDED_CHAT_KEY,
  EMBEDDED_CHAT_VALUE,
} from '~/features/chatbot/constants'
import getAgentAndChatbotDetails, {
  getChatbotSettingsUpdateFromAgent,
} from '~/features/chatbot/hooks/usePopulateChatbot/utils'
import useRefetchGuestAgentOnWindowFocus from '~/features/chatbot/hooks/useRefetchGuestAgentOnWindowFocus'
import { EClientPath } from '~/models/api'
import { store } from '~/store'

const guestAgentQueryOptions = { refetchOnWindowFocus: false }

const usePopulateChatbot = () => {
  const { pathname, search } = useLocation()
  const { agentId, legacyChatbotId, friendlyUrl } = useParams()
  const navigate = useNavigate()
  const setChatbot = store((state) => state.setChatbot)
  const [searchParams] = useSearchParams()
  const isEmbedded = searchParams.get(EMBEDDED_CHAT_KEY) === EMBEDDED_CHAT_VALUE

  const resolvedFriendlyUrl =
    !!agentId || !!legacyChatbotId
      ? ''
      : (friendlyUrl ?? pathname.replaceAll('/', ''))

  const lookupKey = `${agentId ?? ''}|${legacyChatbotId ?? ''}|${resolvedFriendlyUrl}`
  const hasSeededInitialMessagesRef = useRef(false)

  useEffect(() => {
    hasSeededInitialMessagesRef.current = false
  }, [lookupKey])

  const applyAgentToChatbotStore = useCallback(
    (agent: TChatbotAgent) => {
      if (!hasSeededInitialMessagesRef.current) {
        setChatbot(getAgentAndChatbotDetails(agent))
        hasSeededInitialMessagesRef.current = true
        return
      }

      setChatbot(getChatbotSettingsUpdateFromAgent(agent))
    },
    [setChatbot],
  )

  const {
    data: agentDataByFriendlyUrl,
    isError: isErrorAgentByFriendlyUrl,
    isLoading: isLoadingAgentByFriendlyUrl,
    isLoadingError: isLoadingErrorAgentByFriendlyUrl,
  } = useGetAgentByFriendlyUrl({
    friendlyUrl: resolvedFriendlyUrl,
    queryOptions: guestAgentQueryOptions,
  })

  // Retrieve agent by agent id
  const {
    data: agentDataById,
    isError: isErrorAgentById,
    isLoading: isLoadingAgentById,
    isLoadingError: isLoadingErrorAgentById,
  } = useGetGuestAgent({
    agentId: agentId ?? '',
    queryOptions: guestAgentQueryOptions,
  })

  // Retrieve agent by legacy chatbot id
  const {
    data: agentDataByLegacyChatbotId,
    isError: isErrorAgentByLegacyChatbotId,
    isLoading: isLoadingAgentByLegacyChatbotId,
    isLoadingError: isLoadingErrorAgentByLegacyChatbotId,
  } = useGetAgentByLegacyChatbotId({
    legacyChatbotId: legacyChatbotId ?? '',
    queryOptions: guestAgentQueryOptions,
  })

  useRefetchGuestAgentOnWindowFocus({
    agentId,
    legacyChatbotId,
    friendlyUrl,
    pathname,
  })

  // Populate agent and chatbot details by friendly url
  useEffect(() => {
    if (
      !isLoadingAgentByFriendlyUrl &&
      !isErrorAgentByFriendlyUrl &&
      agentDataByFriendlyUrl?.data
    ) {
      applyAgentToChatbotStore(agentDataByFriendlyUrl.data)
    }
  }, [
    agentDataByFriendlyUrl,
    isLoadingAgentByFriendlyUrl,
    isErrorAgentByFriendlyUrl,
    applyAgentToChatbotStore,
  ])

  // Populate agent and chatbot details by agent id
  useEffect(() => {
    if (!isLoadingAgentById && !isErrorAgentById && agentDataById?.data) {
      applyAgentToChatbotStore(agentDataById.data)
    }
  }, [
    agentDataById,
    isLoadingAgentById,
    isErrorAgentById,
    applyAgentToChatbotStore,
  ])

  // Populate agent and chatbot details by agent id using legacy id
  useEffect(() => {
    if (
      !isLoadingAgentByLegacyChatbotId &&
      !isErrorAgentByLegacyChatbotId &&
      agentDataByLegacyChatbotId?.data
    ) {
      applyAgentToChatbotStore(agentDataByLegacyChatbotId.data)
    }
  }, [
    agentDataByLegacyChatbotId,
    isLoadingAgentByLegacyChatbotId,
    isErrorAgentByLegacyChatbotId,
    applyAgentToChatbotStore,
  ])

  useEffect(() => {
    const hasFatalLookupError =
      isLoadingErrorAgentByFriendlyUrl ||
      isLoadingErrorAgentById ||
      isLoadingErrorAgentByLegacyChatbotId
    if (!hasFatalLookupError) return

    // Redirecting to the not-found page would break out of the iframe an
    // embedded chat is loaded in, so !isEmbedded still gates that — but the
    // failure itself must not go silent for the embedded case just because
    // it isn't redirected. Previously this whole effect was a no-op when
    // embedded, so a bad/stale agentId baked into a customer's embed
    // snippet produced an empty-looking widget shell with zero signal
    // anywhere (no console error, nothing) — indistinguishable from a
    // slow-loading widget, which is what made this bug invisible until a
    // customer happened to notice and report it (Loom-reported: "the chat
    // loaded, but the initial message was missing"). ChatBotLayout's own
    // isError-driven fallback message is what actually informs the visitor;
    // this logs so the failure is diagnosable from browser console/log
    // aggregation without needing a customer report first.
    /* eslint-disable-next-line no-console */
    console.error('[Chatbot] Failed to load agent/chatbot settings', {
      agentId,
      legacyChatbotId,
      friendlyUrl,
      isEmbedded,
    })

    if (!isEmbedded) {
      navigate(EClientPath.notFound, {
        state: { from: `${pathname}${search}` },
      })
    }
  }, [
    isLoadingErrorAgentByFriendlyUrl,
    isLoadingErrorAgentById,
    isLoadingErrorAgentByLegacyChatbotId,
    navigate,
    isEmbedded,
    pathname,
    search,
    agentId,
    legacyChatbotId,
    friendlyUrl,
  ])

  const isAgentLookupFatalError =
    isLoadingErrorAgentByFriendlyUrl ||
    isLoadingErrorAgentById ||
    isLoadingErrorAgentByLegacyChatbotId

  return {
    isAgentLookupFatalError,
    isLoading:
      isLoadingAgentByFriendlyUrl ||
      isLoadingAgentById ||
      isLoadingAgentByLegacyChatbotId,
  }
}

export default usePopulateChatbot
